// src/client/renderer/shaders/cape-shader.ts
//
// Cape shader factory (Plan 13, Task 3.1).
//
// Mirrors the wind-sway/waterfall shader-factory pattern: produce one fresh
// THREE.ShaderMaterial per call so different cape meshes get independent
// uniform state (uTime advances per-mesh; uTexture differs per-cape).
//
// Material side: THREE.DoubleSide (cape backside visible when the camera
// orbits behind the character).
//
// Context7 (mrdoob/three.js):
//   "const material = new THREE.ShaderMaterial({ uniforms, vertexShader,
//    fragmentShader, side: THREE.DoubleSide });"
// Source: https://github.com/mrdoob/three.js/blob/dev/examples/webgl_gpgpu_birds.html

import * as THREE from 'three';
import type { ShaderFactory } from './types';
import { VERTEX as CAPE_VERTEX, FRAGMENT as CAPE_FRAGMENT } from './cape-shader.glsl';

export interface CapeShaderUniforms {
  /** Per-frame time in seconds. Consumer advances. Default: 0. */
  uTime: THREE.IUniform<number>;
  /** Sway intensity (max horizontal displacement, world units). Default: 0.1. */
  uIntensity: THREE.IUniform<number>;
  /** Sway speed (radians/second). Default: 1.5. */
  uSpeed: THREE.IUniform<number>;
  /** Stiffness (vertex Y exponent). Lower = whippy, higher = stiff. Default: 1.5. */
  uStiffness: THREE.IUniform<number>;
  /** Tint color multiplied into the sampled texture. Default: (1,1,1) = unmodified. */
  uBaseColor: THREE.IUniform<THREE.Color>;
  /** Per-cape PNG. Caller (attachCape) sets this; default null until first attach. */
  uTexture: THREE.IUniform<THREE.Texture | null>;
}

export const createCapeShader: ShaderFactory = () => {
  const uniforms: CapeShaderUniforms = {
    uTime: { value: 0 },
    uIntensity: { value: 0.1 },
    uSpeed: { value: 1.5 },
    uStiffness: { value: 1.5 },
    uBaseColor: { value: new THREE.Color(1, 1, 1) },
    uTexture: { value: null },
  };
  return new THREE.ShaderMaterial({
    uniforms: uniforms as unknown as Record<string, THREE.IUniform<unknown>>,
    vertexShader: CAPE_VERTEX,
    fragmentShader: CAPE_FRAGMENT,
    side: THREE.DoubleSide,
  });
};
